import { useEffect, useState } from 'react'
import AnimationLayout from './components/AnimationLayout'

interface Badge {
  name: string
  emoji: string
  description: string
  points_awarded: number
}

interface Props {
  animation: { id: number; name: string; description: string }
  badge: Badge
  all_animations: { id: number; name: string }[]
}

export default function Animation29({ animation, badge, all_animations }: Props) {
  const [showBadge, setShowBadge] = useState(false)
  const [typedText, setTypedText] = useState('')
  const [showCursor, setShowCursor] = useState(true)

  const fullText = `Badge débloqué : ${badge.name}`

  useEffect(() => {
    setTimeout(() => setShowBadge(true), 300)

    // Typewriter effect
    let index = 0
    const typeInterval = setInterval(() => {
      if (index < fullText.length) {
        setTypedText(fullText.slice(0, index + 1))
        index++
      } else {
        clearInterval(typeInterval)
        // Clignotement du curseur
        const cursorInterval = setInterval(() => {
          setShowCursor(prev => !prev)
        }, 500)
        setTimeout(() => {
          clearInterval(cursorInterval)
          setShowCursor(false)
        }, 3000)
      }
    }, 80)

    return () => clearInterval(typeInterval)
  }, [])

  return (
    <AnimationLayout animation={animation} allAnimations={all_animations}>
      <style>{`
        @keyframes blink {
          0%, 50% { opacity: 1; }
          51%, 100% { opacity: 0; }
        }
        .cursor {
          animation: blink 1s step-end infinite;
        }
        @keyframes popIn {
          0% { transform: scale(0); opacity: 0; }
          60% { transform: scale(1.2); }
          100% { transform: scale(1); opacity: 1; }
        }
        .pop-in {
          animation: popIn 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
        }
      `}</style>

      <div className="flex flex-col items-center justify-center min-h-[50vh] sm:min-h-[60vh]">
        {/* Terminal-like text */}
        <div className="bg-tessan-green-hover/80 rounded-lg p-6 mb-8 font-mono border border-tessan-yellow/30 min-w-[400px]">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-3 h-3 rounded-full bg-red-500" />
            <div className="w-3 h-3 rounded-full bg-yellow-500" />
            <div className="w-3 h-3 rounded-full bg-green-500" />
          </div>
          <div className="text-tessan-cream">
            <span className="text-tessan-yellow">$ </span>
            <span>{typedText}</span>
            {showCursor && <span className="cursor text-tessan-yellow">|</span>}
          </div>
        </div>

        {/* Badge */}
        <div className={`${showBadge ? 'pop-in' : 'opacity-0'}`}>
          <div className="w-32 h-32 sm:w-44 sm:h-44 bg-tessan-cream rounded-2xl flex items-center justify-center shadow-2xl shadow-tessan-yellow/50">
            <span className="text-6xl sm:text-8xl">{badge.emoji}</span>
          </div>
        </div>

        <div className={`mt-8 text-center transition-all duration-700 delay-500 ${typedText.length === fullText.length ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
          <div className="text-lg sm:text-xl text-tessan-cream">+{badge.points_awarded} points</div>
          <div className="text-tessan-cream/70 mt-4 max-w-[280px] sm:max-w-xs">{badge.description}</div>
        </div>
      </div>
    </AnimationLayout>
  )
}
